// Klucz urządzenia. Rodzic paruje urządzenie swoim PIN-em (POST /api/pair), serwer wydaje klucz `nk_…`, a aplikacja zapisuje go w przeglądarce
// i wysyła z każdym żądaniem tokenu Azure. Sam PIN nigdy nie jest zapisywany. Serwer trzyma tylko skrót klucza.
import type { KeyValueStore } from './progress.ts';

export const DEVICE_STORAGE_KEY = 'naative.device.v1';
const KEY_PATTERN = /^nk_[A-Za-z0-9_-]{20,100}$/;

export interface DeviceInfo {
  key: string;
  deviceId: string;
  /** Nazwa nadana przy parowaniu (np. „Tablet Ani”) — pomaga rodzicowi rozpoznać urządzenie na liście. */
  name: string;
  pairedAt: string;
}

export interface DeviceStore {
  get(): DeviceInfo | null;
  /** false = nie udało się zapisać na stałe (np. tryb prywatny) — urządzenie jest sparowane tylko do zamknięcia karty. */
  set(info: DeviceInfo): boolean;
  clear(): void;
  /** Nagłówki do żądań do serwera (puste, gdy urządzenie nie jest sparowane). */
  headers(): Record<string, string>;
}

function browserStorage(): KeyValueStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null; // przeglądarka potrafi rzucić wyjątek już przy samym dostępie do localStorage
  }
}

function parseDevice(raw: string | null | undefined): DeviceInfo | null {
  if (!raw) return null;
  try {
    const d = JSON.parse(raw) as Partial<DeviceInfo> | null;
    if (!d || typeof d.key !== 'string' || !KEY_PATTERN.test(d.key)) return null;
    return { key: d.key, deviceId: String(d.deviceId ?? ''), name: String(d.name ?? ''), pairedAt: String(d.pairedAt ?? '') };
  } catch {
    return null;
  }
}

export function createDeviceStore(store: KeyValueStore | null = browserStorage()): DeviceStore {
  let memory: DeviceInfo | null = null; // gdy zapis się nie uda, klucz żyje przynajmniej do odświeżenia strony
  const api: DeviceStore = {
    get() {
      if (memory) return memory;
      try {
        return parseDevice(store?.getItem(DEVICE_STORAGE_KEY));
      } catch {
        return null;
      }
    },
    set(info) {
      memory = info;
      try {
        if (!store) return false;
        store.setItem(DEVICE_STORAGE_KEY, JSON.stringify(info));
        memory = null; // zapisane na stałe — pamięć podręczna niepotrzebna
        return true;
      } catch {
        return false;
      }
    },
    clear() {
      memory = null;
      try {
        store?.removeItem?.(DEVICE_STORAGE_KEY);
      } catch {
        /* ignore */
      }
    },
    headers() {
      const out: Record<string, string> = {};
      const d = api.get();
      if (d) out.authorization = `Bearer ${d.key}`;
      return out;
    },
  };
  return api;
}

// --- parowanie ----------------------------------------------------------------------------------

/**
 * Urządzenie zwalnia swoje miejsce na serwerze (POST /api/unpair z własnym kluczem). Wołamy to przed skasowaniem klucza (wylogowanie, „Odłącz to urządzenie”)
 * i po ponownym sparowaniu (stary klucz przestaje być potrzebny). Najlepsza próba: bez sieci, bez serwera albo przy błędzie nic się nie dzieje i nic nie rzuca
 * — serwer i tak wyłączy najdawniej używane urządzenie, gdy zabraknie miejsc przy kolejnym parowaniu. Zwraca true, gdy serwer potwierdził.
 */
export async function releaseDevice(
  key: string,
  fetchImpl: typeof fetch = (...args) => fetch(...args),
  url = './api/unpair',
  timeoutMs = 4000,
): Promise<boolean> {
  if (!KEY_PATTERN.test(key)) return false;
  // AbortController + setTimeout zamiast AbortSignal.timeout: ta druga nie istnieje w Safari przed 16, a wylogowanie nie może wisieć bez końca
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetchImpl(url, { method: 'POST', cache: 'no-store', headers: { authorization: `Bearer ${key}` }, signal: controller.signal });
    return res.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

export type PairFailure =
  | 'bad-pin' // zły PIN
  | 'empty-pin' // pole PIN puste
  | 'too-many' // zbyt wiele błędnych prób z tego adresu IP
  | 'device-limit' // serwer ma już maksymalną liczbę urządzeń (starszy serwer; nowy sam wyłącza najdawniej używane)
  | 'no-server' // ten adres nie ma backendu (statyczny hosting, serwer deweloperski)
  | 'not-configured' // backend jest, ale brakuje bazy albo PARENT_PIN
  | 'network'; // brak połączenia

export type PairResult =
  | { ok: true; device: DeviceInfo; persisted: boolean }
  | { ok: false; reason: PairFailure; retryAfterMin?: number; detail?: string };

interface PairBody {
  key?: unknown;
  deviceId?: unknown;
  error?: unknown;
  retryAfterSec?: unknown;
}

export async function pairDevice(
  pin: string,
  name: string,
  devices: DeviceStore,
  fetchImpl: typeof fetch = (...args) => fetch(...args),
  url = './api/pair',
  now: () => Date = () => new Date(),
  unpairUrl = './api/unpair',
): Promise<PairResult> {
  const cleanPin = pin.trim();
  if (!cleanPin) return { ok: false, reason: 'empty-pin' };
  let res: Response;
  try {
    res = await fetchImpl(url, { method: 'POST', cache: 'no-store', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ pin: cleanPin, name: name.trim().slice(0, 40) }) });
  } catch (e) {
    return { ok: false, reason: 'network', detail: e instanceof Error ? e.message : String(e) };
  }
  let body = null as PairBody | null;
  try {
    body = (await res.json()) as PairBody | null;
  } catch {
    body = null; // np. strona HTML ze statycznego hostingu
  }
  if (res.ok) {
    if (!body || typeof body.key !== 'string' || !KEY_PATTERN.test(body.key)) return { ok: false, reason: 'no-server', detail: 'odpowiedź nie zawiera klucza urządzenia' };
    const device: DeviceInfo = { key: body.key, deviceId: String(body.deviceId ?? ''), name: name.trim().slice(0, 40), pairedAt: now().toISOString() };
    const previous = devices.get(); // ponowne parowanie na sparowanym urządzeniu: stary klucz zwalnia miejsce, zamiast zostać martwym wpisem
    const persisted = devices.set(device);
    // tylko gdy nowy klucz zapisał się na stałe — inaczej po odświeżeniu strony zostałby w pamięci stary, już usunięty z serwera
    if (previous && persisted && previous.key !== device.key) void releaseDevice(previous.key, fetchImpl, unpairUrl);
    return { ok: true, device, persisted };
  }
  const error = typeof body?.error === 'string' ? body.error : '';
  if (res.status === 401 && error === 'bad-pin') return { ok: false, reason: 'bad-pin' };
  if (res.status === 429) return { ok: false, reason: 'too-many', retryAfterMin: Math.max(1, Math.round((Number(body?.retryAfterSec) || 900) / 60)) };
  if (res.status === 409) return { ok: false, reason: 'device-limit' };
  if (res.status === 503 && body) return { ok: false, reason: 'not-configured', detail: error };
  return { ok: false, reason: 'no-server', detail: `HTTP ${res.status}` };
}

/** Komunikat dla rodzica po nieudanym parowaniu. */
export function pairFailureText(r: Extract<PairResult, { ok: false }>): string {
  switch (r.reason) {
    case 'empty-pin':
      return 'Wpisz PIN rodzica.';
    case 'bad-pin':
      return 'Zły PIN. Spróbuj jeszcze raz.';
    case 'too-many':
      return `Za dużo błędnych prób. Spróbuj za ok. ${r.retryAfterMin ?? 15} min.`;
    case 'device-limit':
      return 'Serwer ma już maksymalną liczbę sparowanych urządzeń. Któreś trzeba wyłączyć w panelu rodzica („Dla rodzica” ▸ Panel rodzica) — wejdziesz do niego tym samym PIN-em.';
    case 'not-configured':
      return `Serwer nie jest jeszcze skonfigurowany${r.detail ? `: ${r.detail}` : '.'}`;
    case 'network':
      return 'Brak połączenia z serwerem. Sprawdź internet i spróbuj jeszcze raz.';
    case 'no-server':
      return 'Ten adres nie ma serwera aplikacji, więc nie ma z czym się parować (parowanie działa po wdrożeniu na Cloudflare; lokalnie wystarczą klucze w .env).';
  }
}
